import { Box, Heading, Text } from "@radix-ui/themes";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import styles from "../live-seo-page.module.css";

type AccessibilityStatementLocalization = {
	title: string;
	intro: string;
	commitmentHeading: string;
	commitment: string;
	assistance: string;
	helpHeading: string;
	helpIntro: string;
	helpFollowUp: string;
	responseIntro: string;
	responseItems: string[];
};

const accessibilityStatementLocalization = {
	[Locale.EN]: {
		title: "Accessibility Information",
		intro:
			"Miami Orthodontic Specialists strives to make sure people with disabilities can access our services and website, because we believe in the dignity and well-being of every person.",
		commitmentHeading: "Our Commitment to Accessibility",
		commitment:
			"Our goal is to make our website easy for everyone to use, including people with disabilities. We welcome feedback about how we can improve your experience.",
		assistance:
			"If you need assistance accessing information or using a feature on our website, please contact our office at info@miamibraces.com. Let us know what you need help with and your preferred way for us to respond.",
		helpHeading: "Here to Help You",
		helpIntro:
			"If you have a disability and the format of our web pages interferes with your ability to experience any content on this website, please contact our office at info@miamibraces.com.",
		helpFollowUp:
			"Our office will refer your request to the appropriate person, who will respond to your request.",
		responseIntro:
			"To help us to respond in a manner most helpful to you, please describe:",
		responseItems: [
			"The nature of the accessibility problem",
			"The web address of the requested material",
			"Your preferred format in which you want to receive the material (electronic format (ASCII, etc.), standard print, large print, etc.)",
			"Your contact information",
		],
	},
	[Locale.ES]: {
		title: "Información de accesibilidad",
		intro:
			"Miami Orthodontic Specialists se esfuerza por asegurar que las personas con discapacidades puedan acceder a nuestros servicios y sitio web, porque creemos en la dignidad y el bienestar de cada persona.",
		commitmentHeading: "Nuestro compromiso con la accesibilidad",
		commitment:
			"Nuestro objetivo es que nuestro sitio web sea fácil de usar para todas las personas, incluidas las personas con discapacidades. Agradecemos tus comentarios sobre cómo podemos mejorar tu experiencia.",
		assistance:
			"Si necesitas ayuda para acceder a información o utilizar una función de nuestro sitio web, comunícate con nuestra oficina en info@miamibraces.com. Cuéntanos con qué necesitas ayuda y cómo prefieres que te respondamos.",
		helpHeading: "Estamos aquí para ayudarte",
		helpIntro:
			"Si tienes una discapacidad y el formato de nuestras páginas web interfiere con tu capacidad para experimentar cualquier contenido de este sitio, comunícate con nuestra oficina en info@miamibraces.com.",
		helpFollowUp:
			"Nuestra oficina remitirá tu solicitud a la persona correspondiente, quien responderá a tu solicitud.",
		responseIntro:
			"Para ayudarnos a responder de la manera más útil para ti, describe:",
		responseItems: [
			"La naturaleza del problema de accesibilidad",
			"La dirección web del material solicitado",
			"El formato preferido en el que deseas recibir el material (formato electrónico (ASCII, etc.), impresión estándar, letra grande, etc.)",
			"Tu información de contacto",
		],
	},
} satisfies Record<Locale, AccessibilityStatementLocalization>;

export function AccessibilityStatementPage() {
	const getLocalization = useLocalization(accessibilityStatementLocalization);
	const responseItems = getLocalization("responseItems");

	return (
		<Box className={styles.simplePageBand}>
			<Box className={styles.simplePageInner}>
				<Box className={styles.termsCopy}>
					<Heading as="h1" className={styles.simplePageTitle}>
						{getLocalization("title")}
					</Heading>
					<Text as="p" size="4">
						{getLocalization("intro")}
					</Text>
					<Text as="p" size="4">
						<strong>{getLocalization("commitmentHeading")}</strong>
					</Text>
					<Text as="p" size="4">
						{getLocalization("commitment")}
					</Text>
					<Text as="p" size="4">
						{getLocalization("assistance")}
					</Text>
					<Text as="p" size="4">
						<strong>{getLocalization("helpHeading")}</strong>
					</Text>
					<Text as="p" size="4">
						{getLocalization("helpIntro")}
					</Text>
					<Text as="p" size="4">
						{getLocalization("helpFollowUp")}
					</Text>
					<Text as="p" size="4">
						{getLocalization("responseIntro")}
						{responseItems.map((item) => (
							<span key={item}>
								<br />- {item}
							</span>
						))}
					</Text>
				</Box>
			</Box>
		</Box>
	);
}
